import { useState, useEffect, useRef } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import {
  Home,
  Compass,
  Newspaper,
  MessageSquare,
  Bell,
  User,
  Settings,
  Sun,
  Moon,
  Globe,
  LogOut,
  Search,
  Menu,
  X,
  Shield,
  Flag,
  Plus,
  ChevronDown,
  Bookmark,
  MessageCircle,
  HeartHandshake,
  Sparkles,
  Megaphone,
} from 'lucide-react'

export default function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()
  const { t, i18n } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [theme, setTheme] = useTheme()
  const searchRef = useRef<HTMLInputElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const moreMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const savedLang = localStorage.getItem('language')
    if (savedLang && (savedLang === 'tr' || savedLang === 'en')) {
      i18n.changeLanguage(savedLang)
    }
  }, [i18n])

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/notifications/unread-count').then(r => setUnreadCount(r.data.count)).catch(() => {})
    }
  }, [isAuthenticated, location.pathname])

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus()
    }
  }, [searchOpen])

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleLang = () => {
    const next = i18n.language === 'tr' ? 'en' : 'tr'
    i18n.changeLanguage(next)
    localStorage.setItem('language', next)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchQuery('')
      setSearchOpen(false)
      setMobileOpen(false)
    }
  }

  const mainNav = [
    { icon: Home, label: t('nav.home'), path: '/' },
    { icon: Compass, label: t('nav.explore'), path: '/explore' },
    { icon: Newspaper, label: t('nav.feed'), path: '/feed' },
    { icon: MessageCircle, label: t('nav.forum'), path: '/forum' },
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Top Navbar */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 min-h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src="/apple-touch-icon.png"
              alt="Nyxeverix"
              className="w-8 h-8 rounded-lg object-cover"
              loading="eager"
            />
            <span className="text-lg font-bold hidden sm:block">Nyxeverix</span>
          </Link>

          {/* Desktop Nav — 4 ana link + More dropdown */}
          <nav className="hidden lg:flex items-center gap-1">
            {mainNav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            ))}

            {/* More dropdown — brings all mobile features to desktop */}
            <div className="relative" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  moreMenuOpen
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <span>{t('nav.more')}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {moreMenuOpen && (
                <div className="absolute left-0 top-full mt-2 w-72 rounded-xl bg-card border border-border shadow-xl py-2 z-50 max-h-[80vh] overflow-y-auto">
                  <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('nav.features')}
                  </div>
                  <div className="grid grid-cols-2 gap-1 px-2">
                    <Link to="/daily-light" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Sun className="w-4 h-4" /> {t('nav.dailyLight')}
                    </Link>
                    <Link to="/mood-map" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Compass className="w-4 h-4" /> {t('nav.moodMap')}
                    </Link>
                    <Link to="/events" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Newspaper className="w-4 h-4" /> {t('nav.events')}
                    </Link>
                    <Link to="/time-capsule" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Bookmark className="w-4 h-4" /> {t('nav.timeCapsule')}
                    </Link>
                    <Link to="/polls" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Sparkles className="w-4 h-4" /> {t('nav.polls')}
                    </Link>
                  </div>

                  <div className="border-t border-border my-1.5" />
                  <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('nav.supportCommunity')}
                  </div>
                  <div className="grid grid-cols-2 gap-1 px-2">
                    <Link to="/support" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <MessageCircle className="w-4 h-4" /> {t('nav.support')}
                    </Link>
                    <Link to="/sos" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-destructive hover:text-destructive hover:bg-destructive/5 transition-colors">
                      <Shield className="w-4 h-4" /> {t('nav.sos')}
                    </Link>
                    <Link to="/shout-room" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Megaphone className="w-4 h-4" /> {t('nav.shoutRoom')}
                    </Link>
                    <Link to="/recovery-stories" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <HeartHandshake className="w-4 h-4" /> {t('nav.recoveryStories')}
                    </Link>
                    <Link to="/gratitude" onClick={() => setMoreMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                      <Sun className="w-4 h-4" /> {t('nav.gratitude')}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nav.searchPlaceholder') || 'Search...'}
                  className="w-40 lg:w-60 px-3 py-1.5 rounded-lg bg-secondary border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <button type="button" onClick={() => setSearchOpen(false)} className="p-2 rounded-lg hover:bg-secondary">
                  <X className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                aria-label={t('nav.search')}
              >
                <Search className="w-5 h-5" />
              </button>
            )}

            {isAuthenticated && (
              <>
                {/* Quick Post Button */}
                <Link
                  to="/feed"
                  className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden xl:inline">{t('feed.post')}</span>
                </Link>

                {/* Messages */}
                <Link
                  to="/messages"
                  className="hidden md:flex p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors relative"
                  aria-label={t('nav.messages')}
                >
                  <MessageSquare className="w-5 h-5" />
                </Link>

                {/* Notifications with count badge */}
                <Link
                  to="/notifications"
                  className="hidden md:flex p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors relative"
                  aria-label={t('nav.notifications')}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              </>
            )}

            {/* Auth: Avatar Dropdown */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-secondary transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                    {user?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground hidden md:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-60 rounded-xl bg-card border border-border shadow-lg py-2 z-50">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-medium text-sm">{user?.username}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <div className="flex gap-1.5 mt-2">
                        {user?.role === 'admin' && (
                          <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Admin</span>
                        )}
                        {user?.role === 'moderator' && (
                          <span className="text-[10px] bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full font-medium">Moderator</span>
                        )}
                        {user?.expertType && (
                          <span className="text-[10px] bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded-full font-medium">{user.expertType}</span>
                        )}
                      </div>
                    </div>

                    {/* Menu items */}
                    <Link
                      to={`/profile/${user?.username || ''}`}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                    >
                      <User className="w-4 h-4 text-muted-foreground" />
                      {t('nav.profile')}
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                    >
                      <Settings className="w-4 h-4 text-muted-foreground" />
                      {t('nav.settings')}
                    </Link>
                    <Link
                      to="/notifications"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors md:hidden"
                    >
                      <Bell className="w-4 h-4 text-muted-foreground" />
                      {t('nav.notifications')}
                      {unreadCount > 0 && (
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                          {unreadCount}
                        </span>
                      )}
                    </Link>

                    {/* Role-based panel links */}
                    {user?.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-primary hover:bg-primary/5 transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        {t('nav.adminPanel')}
                      </Link>
                    )}
                    {(user?.role === 'moderator' || user?.role === 'admin') && (
                      <Link
                        to="/moderator"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-amber-500 hover:bg-amber-500/5 transition-colors"
                      >
                        <Flag className="w-4 h-4" />
                        {t('nav.moderatorPanel')}
                      </Link>
                    )}
                    {user?.expertType && (
                      <Link
                        to="/expert"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-emerald-500 hover:bg-emerald-500/5 transition-colors"
                      >
                        <HeartHandshake className="w-4 h-4" />
                        {t('expert.panelTitle')}
                      </Link>
                    )}

                    {/* Divider */}
                    <div className="border-t border-border my-1" />

                    {/* Theme & Language */}
                    <button
                      type="button"
                      onClick={() => { setTheme(theme === 'dark' ? 'light' : 'dark'); setUserMenuOpen(false) }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                    >
                      {theme === 'dark' ? <Sun className="w-4 h-4 text-muted-foreground" /> : <Moon className="w-4 h-4 text-muted-foreground" />}
                      {theme === 'dark' ? t('settings.light') : t('settings.dark')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { toggleLang(); setUserMenuOpen(false) }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-secondary transition-colors"
                    >
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      {i18n.language === 'tr' ? t('settings.languageEnglish') : t('settings.languageTurkish')}
                    </button>

                    {/* Logout */}
                    <div className="border-t border-border mt-1 pt-1">
                      <button
                        type="button"
                        onClick={() => { logout(); setUserMenuOpen(false) }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/5 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {t('nav.login')}
              </Link>
            )}

            {/* Mobile Toggle */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={mobileOpen ? t('common.close') : t('nav.menu')}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            {mainNav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}

            {/* Features on mobile */}
            <div className="border-t border-border pt-2 space-y-1">
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('nav.dailyLight')}
              </p>
              <Link to="/daily-light" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Sun className="w-4 h-4" /> {t('nav.dailyLight')}
              </Link>
              <Link to="/mood-map" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Compass className="w-4 h-4" /> {t('nav.moodMap')}
              </Link>
            </div>

            <div className="border-t border-border pt-2 space-y-1">
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('nav.events')}
              </p>
              <Link to="/events" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Newspaper className="w-4 h-4" /> {t('nav.events')}
              </Link>
              <Link to="/time-capsule" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Bookmark className="w-4 h-4" /> {t('nav.timeCapsule')}
              </Link>
            </div>

            <div className="border-t border-border pt-2 space-y-1">
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('nav.support')}
              </p>
              <Link to="/support" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <MessageCircle className="w-4 h-4" /> {t('nav.support')}
              </Link>
              <Link to="/sos" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Shield className="w-4 h-4" /> {t('nav.sos')}
              </Link>
              <Link to="/shout-room" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <MessageSquare className="w-4 h-4" /> {t('nav.shoutRoom')}
              </Link>
              <Link to="/recovery-stories" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Bookmark className="w-4 h-4" /> {t('nav.recoveryStories')}
              </Link>
              <Link to="/gratitude" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                <Sun className="w-4 h-4" /> {t('nav.gratitude')}
              </Link>
            </div>

            {isAuthenticated ? (
              <div className="border-t border-border pt-2 space-y-1">
                <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t('nav.profile')}
                </p>
                <Link to={`/profile/${user?.username || ''}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                  <User className="w-4 h-4" /> {t('nav.profile')}
                </Link>
                <Link to="/settings" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                  <Settings className="w-4 h-4" /> {t('nav.settings')}
                </Link>
                <Link to="/messages" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors min-h-[44px]">
                  <MessageSquare className="w-4 h-4" /> {t('nav.messages')}
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-primary hover:bg-primary/5 transition-colors min-h-[44px]">
                    <Shield className="w-4 h-4" /> {t('nav.adminPanel')}
                  </Link>
                )}
                {(user?.role === 'moderator' || user?.role === 'admin') && (
                  <Link to="/moderator" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-amber-500 hover:bg-amber-500/5 transition-colors min-h-[44px]">
                    <Flag className="w-4 h-4" /> {t('nav.moderatorPanel')}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => { logout(); setMobileOpen(false) }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-destructive hover:bg-destructive/5 transition-colors min-h-[44px]"
                >
                  <LogOut className="w-4 h-4" /> {t('nav.logout')}
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors min-h-[48px]"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>
        )}
      </header>
    </>
  )
}
